import { Row } from "antd";
import { COMMUNICATION_TASK_STATUS } from "common/constants";
import TableFilter from "components/tableFilter/tableFilter";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import {
  useFetchAgencyWorkflowStates,
  useFetchBackendConstantsQuery,
} from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationSequencesQuery } from "features/sequencesTable/communicationSequenceAPI";
import CommunicationTasksTable from "pages/communications/communicationTaskTable";
import { useMemo, useState } from "react";

function CommunicationTaskListTab({ communicationMediums }) {
  // React state to track the filters
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: communicationSequences } = useFetchCommunicationSequencesQuery();
  const { data: communicationMethods } = useFetchCommunicationMethodsQuery();
  const { data: accountWorkflowStates } = useFetchAgencyWorkflowStates();
  const hasAiPhoneAgentMedium = useMemo(() => {
    return communicationMediums?.includes("artificial_intelligence_phone_agent");
  }, [communicationMediums]);
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery(
    { isEnabled: true },
    { skip: !hasAiPhoneAgentMedium },
  );

  const initialFilterState = useMemo(
    () => ({
      scheduledDate: null,
      communicationSequenceIds: [],
      communicationMethodIds: [],
      communicationTypes: communicationMediums ?? [],
      communicationTaskStatus: hasAiPhoneAgentMedium ? COMMUNICATION_TASK_STATUS.SUCCESS : null,
      communicationTaskStatuses: [],
      communicationResultItemIds: [],
      accountStatusesInclude: [],
      accountStatusesExclude: [],
    }),
    [communicationMediums, hasAiPhoneAgentMedium],
  );

  const [filterStates, setFilterStates] = useState(initialFilterState);

  const useHistoriesEndpoint =
    hasAiPhoneAgentMedium &&
    filterStates.communicationTaskStatus === COMMUNICATION_TASK_STATUS.SUCCESS;

  function setFilterProperty(name, value) {
    setFilterStates((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  }

  const filteredCommunicationTypes = useMemo(() => {
    if (!communicationMediums || communicationMediums.length === 0) {
      return constants?.communicationMediums ?? [];
    }
    return (
      constants?.communicationMediums?.filter((d) => communicationMediums?.includes(d.value)) ?? []
    );
  }, [communicationMediums, constants]);

  const filteredCommunicationMethods = useMemo(() => {
    if (!communicationMediums || communicationMediums.length === 0) {
      return communicationMethods ?? [];
    }
    return communicationMethods?.filter((d) => communicationMediums?.includes(d.medium)) ?? [];
  }, [communicationMediums, communicationMethods]);

  // Fetch all enabled communication result items once, then filter by type

  const callResultItems = useMemo(
    () => allResultItems?.filter((item) => item.resultType === "call"),
    [allResultItems],
  );
  const communicationResultItems = useMemo(
    () => allResultItems?.filter((item) => item.resultType === "communication"),
    [allResultItems],
  );

  const allDisplayableCommunicationResultOptions = useMemo(
    () => [
      {
        label: "Communication Results",
        options: communicationResultItems?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
      },
      {
        label: "Call Results",
        options: callResultItems?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
      },
    ],
    [communicationResultItems, callResultItems],
  );

  // This is the structure of the filters that will be displayed on the page
  const filteredStructure = [
    {
      title: "Communication Status",
      name: hasAiPhoneAgentMedium ? "communicationTaskStatus" : "communicationTaskStatuses",
      type: "dropdown",
      initialValue: hasAiPhoneAgentMedium
        ? initialFilterState.communicationTaskStatus
        : initialFilterState.communicationTaskStatuses,
      dropdown: {
        ...(hasAiPhoneAgentMedium ? { allowClear: false } : { mode: "multiple" }),
        options: constants?.communicationTaskStatuses?.map((d) => ({
          label: d.display,
          value: d.value,
        })),
        onChange: (event) => {
          setFilterProperty(
            hasAiPhoneAgentMedium ? "communicationTaskStatus" : "communicationTaskStatuses",
            event,
          );
        },
        placeholder: "Select Status...",
      },
    },
    // Use this filter only if communicationMediums contain "artificial_intelligence_phone_agent"
    ...(hasAiPhoneAgentMedium
      ? [
          {
            title: "Communication Results",
            name: "communicationResultItemIds",
            type: "dropdown",
            dropdown: {
              mode: "multiple",
              onChange: (event) => setFilterProperty("communicationResultItemIds", event),
              options: allDisplayableCommunicationResultOptions,
              placeholder: "Select Results...",
              maxTagTextLength: 20,
            },
          },
        ]
      : []),
    {
      title: "Scheduled Date",
      name: "scheduledDate",
      type: "date",
      onChange: (event) => setFilterProperty("scheduledDate", event),
    },
    ...(useHistoriesEndpoint
      ? []
      : [
          {
            title: "Communication Sequence",
            name: "communicationSequenceIds",
            type: "dropdown",
            dropdown: {
              mode: "multiple",
              onChange: (event) => {
                setFilterProperty("communicationSequenceIds", event);
              },
              options: communicationSequences?.map((d) => ({
                label: d.name,
                value: d.id,
              })),
              placeholder: "Select Communication Sequence...",
            },
          },
        ]),
    {
      title: "Communication Method",
      name: "communicationMethodIds",
      type: "dropdown",
      dropdown: {
        mode: "multiple",
        onChange: (event) => {
          setFilterProperty("communicationMethodIds", event);
        },
        options: filteredCommunicationMethods?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Communication Method...",
      },
    },
    ...(communicationMediums === null
      ? [
          {
            title: "Communication Type",
            name: "communicationTypes",
            type: "dropdown",
            initialValue: initialFilterState.communicationTypes,
            dropdown: {
              mode: "multiple",
              onChange: (event) => setFilterProperty("communicationTypes", event),
              options: filteredCommunicationTypes?.map((d) => ({
                label: d.display,
                value: d.value,
              })),
              placeholder: "Select Communication Type...",
            },
          },
        ]
      : []),
    {
      title: "Account Status",
      name: "accountStatuses",
      type: "include/exclude",
      include: {
        mode: "multiple",
        onChange: (event) => setFilterProperty("accountStatusesInclude", event),
        options: accountWorkflowStates?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Account Status...",
      },
      exclude: {
        mode: "multiple",
        onChange: (event) => setFilterProperty("accountStatusesExclude", event),
        options: accountWorkflowStates?.map((d) => ({
          label: d.name,
          value: d.id,
        })),
        placeholder: "Select Account Status...",
      },
    },
  ];

  function convertStateToQueryParams() {
    /**
     * Convert the filter states to query params
     * @returns {
     *  scheduledStartDate: string,
     *  scheduledEndDate: string,
     *  communicationSequenceIds: string[],
     *  communicationMethodIds: string[],
     *  communicationType: string[],
     *  communicationTaskStatuses: string[],
     *  communicationResultItemIds: string[],
     *  accountStatusesInclude: string[],
     *  accountStatusesExclude: string[],
     * } queryParams
     *
     */
    const queryParams = {};

    if (filterStates.scheduledDate) {
      const [scheduledStartDate, scheduledEndDate] = filterStates.scheduledDate;
      if (scheduledStartDate) {
        queryParams.scheduledStartDate = scheduledStartDate;
      }
      if (scheduledEndDate) {
        queryParams.scheduledEndDate = scheduledEndDate;
      }
    }

    if (filterStates.communicationSequenceIds.length > 0) {
      queryParams.sequenceIds = filterStates.communicationSequenceIds;
    }

    if (filterStates.communicationMethodIds.length > 0) {
      queryParams.communicationMethodIds = filterStates.communicationMethodIds;
    }

    if (filterStates.communicationTypes.length > 0) {
      queryParams.communicationMediums = filterStates.communicationTypes;
    }

    if (filterStates.communicationTaskStatus) {
      queryParams.communicationTaskStatuses = [filterStates.communicationTaskStatus];
    } else if (filterStates.communicationTaskStatuses.length > 0) {
      queryParams.communicationTaskStatuses = filterStates.communicationTaskStatuses;
    }

    if (filterStates.communicationResultItemIds.length > 0) {
      queryParams.communicationResultItemIds = filterStates.communicationResultItemIds;
    }

    if (filterStates.accountStatusesInclude.length > 0) {
      queryParams.accountStatusesInclude = filterStates.accountStatusesInclude;
    }

    if (filterStates.accountStatusesExclude.length > 0) {
      queryParams.accountStatusesExclude = filterStates.accountStatusesExclude;
    }

    return {
      filters: queryParams,
    };
  }

  function resetFilters() {
    setFilterStates(initialFilterState);
  }

  return (
    <Row wrap={false}>
      <TableFilter filteredStructure={filteredStructure} onReset={() => resetFilters()} />
      {/* NOTE: Looking for suggestion on the best way to debounce this */}
      <CommunicationTasksTable
        filterParams={convertStateToQueryParams()}
        hasAiPhoneAgentMedium={hasAiPhoneAgentMedium}
        communicationTaskStatus={filterStates.communicationTaskStatus}
      />
    </Row>
  );
}
export default CommunicationTaskListTab;
